/**
 * NEXORA AI - PreferencesStep (Refactorisé Final avec Design System)
 * Epic 1.3 Sprint Final - Clôture Onboarding Premium
 * 
 * Utilise : SelectionCard, StepHeader, ValidationMessage
 * Standard : Goals Step (référence)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, ArrowRight, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '../../providers/ThemeProvider';
import { Button } from '../ui/button';
import SelectionCard, { SelectionCardGrid } from '../ui/selection-card';
import StepHeader from '../ui/step-header';
import ValidationMessage from '../ui/validation-message';

export default function PreferencesStep({ data, onNext, onBack, onData }) {
  const { t } = useTranslation('onboarding');
  const { theme, setTheme } = useTheme();
  const [prefs, setPrefs] = useState(data.preferences || {
    theme: theme || 'dark',
    notifications: 'important',
    ai_frequency: 'medium'
  });
  const [showValidation, setShowValidation] = useState(false);

  const updatePref = (key, value) => {
    const updated = { ...prefs, [key]: value };
    setPrefs(updated);
    if (key === 'theme') setTheme(value);
  };

  const handleNext = () => {
    onData({ preferences: prefs });
    setShowValidation(true);
    setTimeout(() => onNext(), 1200);
  };

  const options = {
    theme: [
      { value: 'light', label: t('preferences_theme_light'), icon: '☀️' },
      { value: 'dark', label: t('preferences_theme_dark'), icon: '🌙' },
      { value: 'system', label: t('preferences_theme_system'), icon: '💻' }
    ],
    notifications: [
      { value: 'all', label: t('preferences_notifications_all'), icon: '🔔' },
      { value: 'important', label: t('preferences_notifications_important'), icon: '⭐' },
      { value: 'none', label: t('preferences_notifications_none'), icon: '🔕' }
    ],
    ai_frequency: [
      { value: 'high', label: t('preferences_ai_frequency_high'), icon: '🚀' },
      { value: 'medium', label: t('preferences_ai_frequency_medium'), icon: '📊' },
      { value: 'low', label: t('preferences_ai_frequency_low'), icon: '🎯' }
    ]
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-4xl"
      >
        {/* Header avec StepHeader */}
        <StepHeader
          icon={<Settings className="text-purple-400" />}
          title={t('preferences_title')}
          subtitle={t('preferences_subtitle')}
        />

        {/* Sections de préférences */}
        <div className="space-y-8 mb-8">
          {Object.entries(options).map(([key, values]) => (
            <div key={key}>
              <h3 className="text-lg font-semibold text-white mb-4">
                {t(`preferences_${key}`)}
              </h3>
              <SelectionCardGrid cols={3}>
                {values.map((option) => (
                  <SelectionCard
                    key={option.value}
                    id={option.value}
                    selected={prefs[key] === option.value}
                    onSelect={() => updatePref(key, option.value)}
                    icon={option.icon}
                    title={option.label}
                    size="sm"
                  />
                ))}
              </SelectionCardGrid>
            </div>
          ))}
        </div>

        {/* Message de valorisation */}
        <ValidationMessage
          show={showValidation}
          type="success"
          message={t('validation_preferences')}
        />

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={onBack}
            className="min-h-[44px] text-zinc-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            <span className="hidden sm:inline">{t('back')}</span>
          </Button>
          <Button
            onClick={handleNext}
            disabled={showValidation}
            className="min-h-[44px] bg-purple-600 hover:bg-purple-500"
          >
            {t('continue')}
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
